import { useTranslation } from "react-i18next";
import type { CreditWalletSnapshot } from "@/features/billing/billing.types";

type CreditBalanceCardProps = {
  wallet: CreditWalletSnapshot;
};

export function CreditBalanceCard({ wallet }: CreditBalanceCardProps) {
  const { t } = useTranslation();
  const allocationCap = wallet.monthlyAllocation > 0
    ? Math.max(wallet.monthlyAllocation, wallet.balance)
    : Math.max(wallet.balance, 1);
  const progressWidth = Math.min(100, (wallet.balance / allocationCap) * 100);

  return (
    <div className="space-y-7">
      <div>
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-ink/78">
          {t("billing.wallet.eyebrow")}
        </p>
      </div>

      <div className="space-y-5">
        <div className="flex flex-wrap items-end gap-4">
          <p className="font-display text-[clamp(3.8rem,8vw,5.2rem)] leading-none text-ink">
            {wallet.balance}
          </p>
          <p className="pb-2 text-[1.8rem] text-ink/54">{t("billing.wallet.available")}</p>
        </div>

        <div className="overflow-hidden rounded-full bg-[#e7dfd5]">
          <div
            className="h-3 rounded-full bg-[#9a7b54] transition-[width]"
            style={{ width: `${progressWidth}%` }}
          />
        </div>

        <p className="text-sm text-ink/58">
          {wallet.allocationResetAt
            ? t("billing.wallet.renewalSummary", {
                defaultValue: "{{count}} credits renew on {{date, datetime}}",
                count: wallet.monthlyAllocation,
                date: wallet.allocationResetAt,
              })
            : t("billing.wallet.balanceHint")}
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-[1.25rem] bg-[#fbf7f1] px-5 py-4 ring-1 ring-[#e7ddd0]">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-ink/42">
            {t("billing.wallet.monthlyAllocation")}
          </p>
          <p className="mt-3 text-xl font-semibold text-ink">
            {t("billing.creditValue", { count: wallet.monthlyAllocation })}
          </p>
        </div>

        <div className="rounded-[1.25rem] bg-[#fbf7f1] px-5 py-4 ring-1 ring-[#e7ddd0]">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-ink/42">
            {t("billing.wallet.nextReset")}
          </p>
          <p className="mt-3 text-xl font-semibold text-ink">
            {wallet.allocationResetAt
              ? t("billing.dateValue", { date: wallet.allocationResetAt })
              : t("billing.wallet.noReset")}
          </p>
        </div>
      </div>
    </div>
  );
}
